import { Check } from "lucide-react"

import { cn } from "@/lib/utils"
import { t } from "@/lib/i18n"

const KEY = "ui.react.pages.staff.registrations.new"

/**
 * What the account is *for*, and the three objections that stop a restaurant
 * owner at the sign-up form: does this cost me now, what does it cost later,
 * and do I have to buy or install anything.
 *
 * The monthly figure is read from the landing's own pricing key rather than
 * restated here. A price living in two locale entries is a price that changes
 * in one of them.
 */
export function RegistrationSupport({ className }: { className?: string }) {
  const proof = [
    { key: "no_card", title: t(`${KEY}.proof_no_card_title`) },
    { key: "price", title: t(`${KEY}.proof_price_title`, { amount: t("ui.landing.pricing.monthly.amount") }) },
    { key: "web", title: t(`${KEY}.proof_web_title`) },
  ]

  return (
    <div className={cn("flex flex-col gap-8", className)}>
      <ol className="flex flex-col">
        <Step
          title={t(`${KEY}.step_account_title`)}
          description={t(`${KEY}.step_account_description`)}
          badge={t(`${KEY}.step_account_now`)}
          current
        />
        <Step
          title={t(`${KEY}.step_location_title`)}
          description={t(`${KEY}.step_location_description`)}
          last
        />
      </ol>

      <div className="flex flex-col">
        {proof.map((row, index) => (
          <div
            key={row.key}
            className={cn(
              "grid grid-cols-[1rem_minmax(0,1fr)] gap-3 border-t py-3.5",
              index === proof.length - 1 && "border-b"
            )}
          >
            <Check className="mt-1 size-4" aria-hidden />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-semibold">{row.title}</span>
              <span className="text-sm leading-6 text-muted-foreground">
                {t(`${KEY}.proof_${row.key}_description`)}
              </span>
            </span>
          </div>
        ))}
      </div>

      <p className="text-sm leading-6 text-muted-foreground">{t(`${KEY}.opens_note`)}</p>
    </div>
  )
}

function Step({
  title,
  description,
  badge,
  current = false,
  last = false,
}: {
  title: string
  description: string
  badge?: string
  current?: boolean
  last?: boolean
}) {
  return (
    <li className="flex gap-3.5">
      <span className="flex flex-col items-center gap-1.5" aria-hidden>
        <span
          className={cn(
            "mt-1.5 size-2 shrink-0 rounded-full",
            current ? "bg-primary" : "border border-muted-foreground"
          )}
        />
        {!last && <span className="w-px flex-1 bg-border" />}
      </span>

      <span className={cn("flex flex-col gap-1", !last && "pb-5")}>
        <span className="flex items-center gap-2">
          <span className="text-sm font-semibold">{title}</span>
          {badge && (
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
              {badge}
            </span>
          )}
        </span>
        <span className="text-sm leading-6 text-muted-foreground">{description}</span>
      </span>
    </li>
  )
}
